import React, { useEffect, useState, useRef, memo } from "react";
import vasConnectInstance from "./vasconnectInstance";
import { useCommon } from "../context/CommonContext";
import { useLanguage } from "../context/LanguageProvider";

/**
 * VasConnectStatusComponent
 *
 * Presentation + logic component for displaying the current connection status
 * to an external VAS device via the vasConnectInstance API.
 *
 * It's wrapped in `memo` so it only re-renders if its props change,
 * which is useful because connection events may fire frequently — we avoid
 * unnecessary re-renders unless sample mode, status, translation function,
 * or loading state really changed.
 *
 * Props:
 *  - sampleMode: string from global context, indicates if the study is in VAS mode or not.
 *  - vasStatus: ref from context that stores the current backend connection status.
 *  - deviceCount: number from context with how many devices are currently connected.
 *  - setDeviceCount: setter for updating the shared deviceCount.
 *  - translation: i18n function from LanguageProvider for translating message keys.
 *  - loading: boolean from LanguageProvider that’s true while translations are still loading.
 */
const VasConnectStatusComponent = memo(
  ({ sampleMode, vasStatus, deviceCount, setDeviceCount, translation, loading }) => {
    console.log("------------------vasconnectstatuscomponent rendered----------------------");

    // Local display status string ("websocket" for connected, "Not Running" otherwise)
    const [status, setStatus] = useState("");

    // Mutable list of confirm dialog requests that should be displayed
    // only after translations have loaded (avoids trying to show untranslated text).
    const pendingConfirms = useRef([]);

    useEffect(() => {
      const vc = vasConnectInstance; // Singleton API instance to talk to VASConnect app

      /**
       * Helper to ask VASConnect for currently connected devices.
       * Updates shared `deviceCount` in global context.
       * If not exactly 1 device, flags status as "Not Running".
       */
      const checkDevices = async () => {
        try {
          const devices = await vc.listDevices();
          console.log("Devices:", devices);
          setDeviceCount(devices.length);
          if (devices.length !== 1) {
            setStatus("Not Running");
          }
        } catch (error) {
          // API call failed — treat as disconnected
          console.error("Error fetching devices:", error);
          setDeviceCount(0);
          setStatus("Not Running");
        }
      };

      /**
       * Checks if we had queued confirm dialogs (e.g., disconnection warnings)
       * while translations were loading and now can display them.
       * This flushes `pendingConfirms` when ready.
       */
      const processPendingConfirms = () => {
        if (!loading && pendingConfirms.current.length > 0) {
          pendingConfirms.current.forEach(({ messageKey }) => {
            window.confirm(translation(messageKey));
          });
          pendingConfirms.current = [];
        }
      };

      /**
       * Subscribe to vasConnectInstance's connection status changes.
       * This triggers when app <-> VASConnect WebSocket link goes up or down.
       * Updates status string, counts devices, and warns user if disconnected mid-session.
       */
      const unsubscribe = vc.onConnectionStatusChange(async (newStatus, oldStatus) => {
        console.log(`Connection status changed: ${oldStatus} -> ${newStatus}`);

        // Only respond if new value differs from current shared ref
        if (newStatus !== vasStatus.current) {
          vasStatus.current = newStatus; // persist in global context
          setStatus(newStatus);

          if (newStatus === "websocket") {
            // Fully connected — check device count
            await checkDevices();
          } else {
            // Not connected — reset device count & display status
            setDeviceCount(0);
            setStatus("Not Running");
          }

          // If we were connected and now lost it, show/wqueue a confirm dialog
          if (oldStatus === "websocket" && newStatus !== "websocket") {
            if (loading) {
              pendingConfirms.current.push({ messageKey: "VAS_connectdisconnection" });
            } else {
              window.confirm(translation("VAS_connectdisconnection"));
            }
          }
        }
      });

      /**
       * Subscribe to specific hardware device disconnection events.
       * Even if websocket remains alive, losing the device triggers
       * status change and user warning.
       */
      const unsubscribeonDeviceDisconnected = vc.onDeviceDisconnected(async (e) => {
        console.log(`Device disconnected: id=${e.device.id}`);
        setStatus("Not Running");
        if (loading) {
          pendingConfirms.current.push({ messageKey: "VAS_disconnection" });
        } else {
          window.confirm(translation("VAS_disconnection"));
        }
      });

      /**
       * Subscribe to device connection events.
       * Marks status as connected when hardware appears.
       */
      const unsubscribeonDeviceConnected = vc.onDeviceConnected((e) => {
        console.log(`Device connected: id=${e.device.id}`);
        setStatus("websocket");
      });

      // In case we queued messages before translation loaded, handle them now (or on next render pass)
      processPendingConfirms();

      // Cleanup all event listeners on unmount to prevent leaks/duplicates
      return () => {
        unsubscribe();
        unsubscribeonDeviceDisconnected();
        unsubscribeonDeviceConnected();
      };
    }, [loading, translation, vasStatus, setDeviceCount]);

    // Don't render anything if this participant/sampleMode isn't using VAS devices
    if (sampleMode !== "VAS") {
      return null;
    }

    // Choose circle background color based on status
    // Green when "websocket" (connected), red otherwise
    let circleClass = status === "websocket" ? "bg-green-500" : "bg-red-500";

    return (
      <div className="absolute top-0 right-0 bg-gray-100 bg-opacity-65 rounded-md z-50 flex items-center">
        {/* When disconnected, show link to download/install VASConnect desktop app */}
        {status !== "websocket" && (
          <a 
            href="https://vasonnect-file.s3.us-east-2.amazonaws.com/vasconnect.exe+files/VASConnect-v1.5.5.exe" 
            target="_blank" 
            rel="noopener noreferrer" 
            className="ml-2 text-blue-400 font-bold underline mr-2"
          >
            Download VASConnect
          </a>
        )}
        {/* Visual status indicator circle: ✔ for connected, X for disconnected */}
        <div className={`w-6 h-6 rounded-full flex items-center justify-center ${circleClass}`}>
          {status !== "websocket" ? (
            <span className="text-white">X</span>
          ) : (
            <span className="text-white">✔</span>
          )}
        </div>
      </div>
    );
  }
);

/**
 * VasConnectStatus
 *
 * Wrapper/container component that pulls the relevant state & actions
 * from shared context providers (`CommonContext` and `LanguageProvider`) and
 * passes them down as props to the memoized VasConnectStatusComponent.
 *
 * This makes the inner component easier to test in isolation
 * and optimises rendering when only some context values change.
 */
const VasConnectStatus = () => {
  console.log("------------------vasconnectstatus rendered----------------------");

  // Pull survey/hardware relevant info from global contexts
  const { sampleMode, vasStatus, deviceCount, setDeviceCount } = useCommon();
  const { translation, loading } = useLanguage();
  
  return (
    <VasConnectStatusComponent
      sampleMode={sampleMode}
      vasStatus={vasStatus}
      deviceCount={deviceCount}
      setDeviceCount={setDeviceCount}
      translation={translation}
      loading={loading}
    />
  );
};

export default VasConnectStatus;
// Also export the vasConnect API singleton for other modules to import and interact with
export { vasConnectInstance };